/* global firebase, VERSION, fetch */

import { ignorePluginDisabledException, captureException } from './errorhandling'
import { getConfig } from './config'
import { hasFeature } from './feature-flags'

var Promise = window.TrelloPowerUp.Promise

const clientVersion = VERSION
const expectAPIVersion = '^1.2.0'
const rcid = Math.random().toString(36).slice(2)

var SYNC_ID = '---'

// These define the local state
var fbSharedStorage = window.sessionStorage
var fbAlertShowing = false
var fbSubscriptionContext = { board: null, organization: null }

export function fbInit () {
  fbSharedStorage.clear()
}

export function fbUpdateToken (t, key = null, token = null) {
  dbgLog('fbUpdateToken', t, key, token)
  const worker = function () {
    dbgLog('fbUpdateToken.worker')
    // auth is globally valid
    return _fbGetCurrentToken().then((currentToken) => {
      dbgLog('*')
      if (token !== currentToken && (!token || token !== fbSharedStorage.getItem('errorToken'))) {
        dbgLog('.', token, currentToken)
        return token ? _fbValidateToken(t, key, token).then(() => _fbSubscribe(t)) : _fbInvalidateToken(t)
      }
    })
  }
  dbgLog('enqueing _fbUpdateToken')
  fbQLogin = fbQLogin.then(worker, worker)
  return fbQLogin
}

export function fbSubscribe (t) {
  dbgLog('fbSubscribe', t)
  const worker = function () {
    dbgLog('fbSubscribe.worker')
    // subscription context is local
    const context = t.getContext()
    if (context.board !== fbSubscriptionContext.board || context.organization !== fbSubscriptionContext.organization) {
      fbSubscriptionContext = context
      return _fbSubscribe(t)
    }
  }
  dbgLog('enqueing _fbSubscribe')
  fbQLogin = fbQLogin.then(worker, worker)
  return fbQLogin
}

export function fbGetConsent (t) {
  const user = firebase.auth().currentUser
  if (user) {
    return firebase.firestore().collection('users').doc(t.getContext().member).get().then(doc => doc.exists && doc.data().consent).catch(captureException)
  } else {
    return Promise.resolve('unknown')
  }
}

export function fbSetConsent (t, consent) {
  const user = firebase.auth().currentUser
  if (user) {
    return firebase.firestore().collection('users').doc(t.getContext().member).set({ consent: consent }, { merge: true }).catch(captureException)
  } else {
    throw new Error('User not authenticated')
  }
}

export function fbClearSessionCache (t) {
  var itemName = t.getContext().board + '.' + t.getContext().organization + '.sessionEOL'
  fbSharedStorage.removeItem(itemName)
}

//
// Internal functions and members
//

var fbQLogin = Promise.resolve()

var fbCancelSubscriptionFn = () => {}
var fbSessionRenewalHandle = null

const _fbValidateToken = function (t, key, token) {
  dbgLog('_fbValidateToken', key, token)
  t.alert({ message: 'Starting up...', duration: 10 }).catch(ignorePluginDisabledException)
  fbAlertShowing = true

  const auth = firebase.auth()
  return Promise.all([
    auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL),
    callAPI('auth', { key: key, token: token /*, board: fbLatestTAlert.getContext().board, organization: fbLatestTAlert.getContext().organization */ })
  ]).spread((_, res) => {
    dbgLog('res', res)
    return (res.data.status === 'ok') ? auth.signInWithCustomToken(res.data.token)
      .then(() => {
        fbSharedStorage.removeItem('errorToken')
        fbSharedStorage.setItem('signedIn', true)
      }) : _fbInvalidateToken(t, res.data.status)
  }).catch((err) => {
    console.error('Unexpected error while authenticating with backend', err)
    return _fbInvalidateToken(t, 'unknown error')
  })
}

const _fbInvalidateToken = function (t, error) {
  dbgLog('_fbInvalidateToken', error)
  const p = error ? _fbGetCurrentToken().then((token) => {
    fbSharedStorage.setItem('errorReason', error)
    fbSharedStorage.setItem('errorToken', token)
  }) : Promise.resolve()
  dbgLog('Unsubscribe (just in case)')
  fbCancelSubscriptionFn()
  fbCancelSubscriptionFn = () => {}
  fbClearSessionCache(t)
  _fbResetSessionRenewalTimeout(null)
  const eolItem = t.getContext().board + '.' + t.getContext().organization + '.sessionEOL'
  fbSharedStorage.removeItem(eolItem)
  dbgLog('Signing out')
  return p.then(() => firebase.auth().signOut())
    .then(() => fbSharedStorage.removeItem('signedIn'))
    .then(() => t.set('board', 'private', 'changed', Date.now()).catch(_ => {})) // Safely ignore rejections here
    .then(() => dbgLog('Signed Out'))
    .then(() => {
      if (error === 'invalid token') {
        dbgLog('Clearing token')
        return t.getRestApi().clearToken()
      }
    })
}

const _fbSubscribe = function (t) {
  dbgLog('_fbSubscribe', t)
  dbgLog('Unsubscribe (just in case)')
  fbCancelSubscriptionFn()
  if (firebase.auth().currentUser && fbSubscriptionContext.board) {
    dbgLog('preparing to subscribe to', fbSubscriptionContext)
    return _fbEnsureSession(t, () => {
      const db = firebase.firestore()
      dbgLog('subscribing to', fbSubscriptionContext)
      fbCancelSubscriptionFn = db.collection('instances').doc(fbSubscriptionContext.board).collection('tasks').onSnapshot({
        next: (snapshot) => _fbSnapshotNext(t, fbSubscriptionContext.board, snapshot),
        error: (error) => _fbSnapshotError(t, error)
      })
      _fbResetSessionRenewalTimeout(t)
      dbgLog('subscribed')
      return Promise.resolve({ data: { status: 'ok' } })
    })
  } else {
    dbgLog('waiting to subscribe, either user not authenticated or board not set')
    fbCancelSubscriptionFn = () => {}
    return Promise.resolve()
  }
}

const _fbEnsureSession = function (t, fn, retry = true) {
  dbgLog('_fbEnsureSession', fn, retry)
  const itemName = t.getContext().board + '.' + t.getContext().organization + '.sessionEOL'
  const eol = fbSharedStorage.getItem(itemName)
  dbgLog('Now:', Date.now(), 'eol:', eol)
  const p = (eol && parseInt(eol) >= Date.now()) ? Promise.resolve({ data: { status: 'ok' } }) : _fbAcquireSession(t)
  return p.then((res) => (res.data.status === 'ok') ? fn() : res).then((res) => {
    return _fbCheckBackendResponse(t, res, retry ? () => _fbEnsureSession(t, fn, false) : null)
  })
}

const _fbCheckBackendResponse = function (t, res, retryFn = null) {
  dbgLog('_fbCheckBackendResponse', res, retryFn)
  switch (res.data.status) {
    case 'ok':
      return res
    case 'invalid token':
      dbgLog('Clearing token')
      return _fbInvalidateToken(t, 'invalid token').then(() => res)
    case 'invalid board session':
      var itemName = t.getContext().board + '.' + t.getContext().organization + '.sessionEOL'
      fbSharedStorage.removeItem(itemName)
      return retryFn ? retryFn() : res
    default:
      return res
  }
}

const _fbAcquireSession = function (t) {
  dbgLog('_fbAcquireSession', t)
  return checkBoardPrivileges(t)
    .then((res) => {
      dbgLog('res', res)
      if (res.data.status === 'ok') {
        const itemName = t.getContext().board + '.' + t.getContext().organization + '.sessionEOL'
        fbSharedStorage.setItem(itemName, Date.now() + ((res.data.ttl - 30) * 1000))
        // Update session renewal timeout (if applicable)
        if (t.getContext().board === fbSubscriptionContext.board) {
          _fbResetSessionRenewalTimeout(t)
        }
      }
      return res
    })
}

const _fbResetSessionRenewalTimeout = function (t) {
  dbgLog('_fbResetSessionRenewalTimeout', t)
  // Cancel old timeout (if exists)
  if (fbSessionRenewalHandle) clearTimeout(fbSessionRenewalHandle)
  // Register new timeout to fire 30s prior to session expiry
  if (t) {
    const itemName = t.getContext().board + '.' + t.getContext().organization + '.sessionEOL'
    const ttl = parseInt(fbSharedStorage.getItem(itemName)) - Date.now() - 30 * 1000
    dbgLog('Setting timout to', ttl / 1000, 'seconds')
    fbSessionRenewalHandle = setTimeout(() => _fbAcquireSession(t).then((res) => _fbCheckBackendResponse(t, res)), ttl)
  } else {
    fbSessionRenewalHandle = null
  }
}

const _fbSnapshotNext = function (t, boardId, snapshot) {
  dbgLog('_fbSnapshotNext', t, boardId, snapshot)
  const ts = Date.now()
  for (const change of snapshot.docChanges()) {
    const id = boardId + '.' + change.doc.id
    dbgLog('_fnSnapshotNext', 'update card data on', id)
    switch (change.type) {
      case 'added':
      case 'modified':
        fbSharedStorage.setItem(id, JSON.stringify(change.doc.data()))
        break
      case 'removed':
        fbSharedStorage.removeItem(id)
        break
    }
    t.set(change.doc.id, 'private', 'changed', ts).catch(_ => {}) // Safely ignore rejections here
      .finally(() => {
        if (fbAlertShowing) {
          fbAlertShowing = false
          t.hideAlert().catch(ignorePluginDisabledException)
        }
      })
  }
}

const _fbSnapshotError = function (t, error) {
  dbgLog('_fbSnapshotError', error)
  return _fbSubscribe(t)
}

const _fbGetCurrentToken = function () {
  dbgLog('_fbGetCurrentToken')
  const user = firebase.auth().currentUser
  dbgLog('#', user)
  return user ? user.getIdTokenResult().then((res) => res.claims.token) : Promise.resolve(null)
}

export function getTaskData (t, cid = 'card') {
  if (fbSharedStorage.getItem('signedIn')) {
    const bid = t.getContext().board
    if (cid === 'card') {
      cid = t.getContext().card
    }
    const data = fbSharedStorage.getItem(bid + '.' + cid)
    return (data && JSON.parse(data)) || {}
  } else {
    return {}
  }
}

export function getTextualState (data, forSort = false) {
  if (forSort && !data.state) {
    return 'NO STATE'
  }
  if (data.state === 'done') {
    return 'Done'
  }
  if (data.onHold) {
    return 'On Hold'
  }
  return data.state === 'open' ? 'Open' : 'Started'
}

export function checkBoardPrivileges (t) {
  return callAPI('checkBoardPrivileges', { board: t.getContext().board, organization: t.getContext().organization, clientPermissions: t.getContext().permissions })
}

export function attachEmptyProcess (t) {
  return callAPI('attachWorkflow', { board: t.getContext().board, card: t.getContext().card }, t)
}

export function attachProcessFromTemplate (t, templateBoard, templateId, targetList, position) {
  return callAPI('attachWorkflow', { board: t.getContext().board, card: t.getContext().card, templateBoard: templateBoard, templateList: templateId, targetList: targetList, targetListPosition: position }, t)
}

export function detachProcess (t, deleteChildren = false) {
  return callAPI('detachWorkflow', { board: t.getContext().board, card: t.getContext().card, deleteCards: deleteChildren }, t)
}

export function addTask (t, name, position, targetList, targetListPosition, appendHold = false) {
  return callAPI('addStep', { board: t.getContext().board, card: t.getContext().card, name: name, position: position, targetList: targetList, targetListPosition: targetListPosition, appendHold: appendHold }, t)
}

export function addExistingTask (t, id) {
  return callAPI('addExistingStep', { board: t.getContext().board, card: t.getContext().card, card2: id }, t)
}

export function addHoldingPoint (t, position) {
  return addTask(t, SYNC_ID, position)
}

export function removeTask (t, index, deleteCards = false) {
  return callAPI('removeStep', { board: t.getContext().board, card: t.getContext().card, position: index, deleteCards: deleteCards }, t)
}

export function changeChildOrder (t, order) {
  return callAPI('reorderSteps', { board: t.getContext().board, card: t.getContext().card, steps: order }, t)
}

export function setState (t, state) {
  return callAPI('setTaskState', { board: t.getContext().board, card: t.getContext().card, state: state }, t)
}

function getConfigParametersForCmd (t, cmd) {
  let autoMove = false
  const pnames = []
  switch (cmd) {
    case 'setTaskState':
    case 'attachWorkflow':
    case 'detachWorkflow':
    case 'addStep':
    case 'addExistingStep':
    case 'removeStep':
    case 'reorderSteps':
      pnames.push('completeOnDone', 'removeMembersOnDone')
      autoMove = true
  }

  if (autoMove || pnames.length) {
    return getConfig(t).then(conf => {
      // Check if to include auto-move list ids
      if (autoMove && hasFeature('auto_move_cards') && conf.autoMoveCards) {
        pnames.push('openListId', 'startedListId', 'doneListId', 'onHoldListId', 'autoMoveScope')
      }
      // Sanitize potentially missing values
      conf.completeOnDone = conf.completeOnDone === undefined || conf.completeOnDone
      conf.removeMembersOnDone = !!conf.removeMembersOnDone
      // construct param object
      const param = {}
      for (const pname of pnames) {
        param[pname] = conf[pname]
      }
      return param
    })
  } else {
    return Promise.resolve({})
  }
}

const callAPI = function (cmd, data, t = null) {
  return getConfigParametersForCmd(t, cmd).then((cparams) => {
    dbgLog('api call to endpoint', cmd, 'with parameters', data)
    const body = { cmd: cmd, data: { rcid: rcid, clientVersion: clientVersion, expectAPIVersion: expectAPIVersion } }
    Object.assign(body.data, cparams, data)
    const call = () => fbAPIDirect(body)
    if (t) {
      const worker = () => _fbEnsureSession(t, call)
      dbgLog('enqueing callAPI', cmd)
      fbQLogin = fbQLogin.then(worker, worker)
      return fbQLogin
    } else {
      return call()
    }
  })
}

// const fbAPIDirect = firebase.functions().httpsCallable('api')

// let apiHits = 0

const fbAPIDirect = function (data, authenticated = true) {
  // console.log('API hits', ++apiHits)
  const origin = (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') ? 'https://processes4trello-staging.firebaseapp.com' : window.location.origin
  // console.log('Environment:', process.env.NODE_ENV, origin)
  return (authenticated && firebase.auth().currentUser ? firebase.auth().currentUser.getIdToken() : Promise.resolve())
    .then((token) => fetch(origin + '/api', {
      headers: Object.assign({ 'Content-Type': 'application/json' }, token ? { Authorization: 'Bearer ' + token } : {}),
      method: 'POST',
      body: JSON.stringify({ data })
    }))
    .then((response) => response.json())
    .then((json) => {
      if (json.result) {
        return { data: json.result }
      } else if (json.error) {
        throw new Error('Backend request failed for reason ' + json.error.status + ': ' + json.error.message)
      } else {
        throw new Error('Unknown response state from backend: ' + JSON.stringify(json))
      }
    })
}

const dbgLog = function () {
  // console.log(...arguments)
}
